import type {RpcError} from './types';

import {RpcRequestError} from './base';
import {
  ErrorApiUndefined,
  ErrorJwtExpired,
  ErrorJwtInvalid,
  ErrorResourceCannotDelete,
  ErrorResourceFailedConstraint,
  ErrorResourceInvalidContext,
  ErrorResourceQueryInvalidValue,
  ErrorResourceQueryUndefinedField,
  ErrorResourceQueryUnsupportedOperator,
  ErrorResourceUnreachable,
  ErrorResourceUserBanned,
  ErrorResourceUserBannedMethod,
  ErrorResourceUserBlockedAccount,
  ErrorResourceUserExpiredCode,
  ErrorResourceUserExpiredSession,
  ErrorResourceUserInvalidCode,
  ErrorResourceUserTooManyAttempts,
  ErrorResourceUserUndefinedAuthId,
  ErrorRpcAccessDenied,
  ErrorRpcInternal,
  ErrorRpcInvalidParams,
  ErrorRpcInvalidRequest,
  ErrorRpcParse,
  ErrorRpcUndefinedMethod,
  ErrorRpcUnsupportedVersion,
  ErrorSubscriptionExpired,
  ErrorVendorOpentok,
  ErrorVendorRecaptcha,
  ErrorVendorS3,
  ErrorVendorStripe,
  ErrorVendorStripeCard,
  WarningRpcDeprecated,
} from './classes';
import {REASON_SUBSCRIPTION_EXPIRED} from './constants';

const apiErrorsMap = {
  'error.jwt.expired': ErrorJwtExpired,
  'error.jwt.invalid': ErrorJwtInvalid,
  'error.resource.cannot_delete': ErrorResourceCannotDelete,
  'error.resource.failed_constraint': ErrorResourceFailedConstraint,
  'error.resource.invalid_context': ErrorResourceInvalidContext,
  'error.resource.query.invalid_value': ErrorResourceQueryInvalidValue,
  'error.resource.query.undefined_field': ErrorResourceQueryUndefinedField,
  'error.resource.query.unsupported_operator':
    ErrorResourceQueryUnsupportedOperator,
  'error.resource.unreachable': ErrorResourceUnreachable,
  'error.resource.user.banned': ErrorResourceUserBanned,
  'error.resource.user.banned.method': ErrorResourceUserBannedMethod,
  'error.resource.user.blocked_account': ErrorResourceUserBlockedAccount,
  'error.resource.user.expired_code': ErrorResourceUserExpiredCode,
  'error.resource.user.expired_session': ErrorResourceUserExpiredSession,
  'error.resource.user.invalid_code': ErrorResourceUserInvalidCode,
  'error.resource.user.too_many_attempts': ErrorResourceUserTooManyAttempts,
  'error.resource.user.undefined_auth_id': ErrorResourceUserUndefinedAuthId,
  'error.rpc.access_denied': ErrorRpcAccessDenied,
  'error.rpc.internal': ErrorRpcInternal,
  'error.rpc.invalid_params': ErrorRpcInvalidParams,
  'error.rpc.invalid_request': ErrorRpcInvalidRequest,
  'error.rpc.parse': ErrorRpcParse,
  'error.rpc.undefined_method': ErrorRpcUndefinedMethod,
  'error.rpc.unsupported_version': ErrorRpcUnsupportedVersion,
  'error.undefined': ErrorApiUndefined,
  'error.vendor.opentok': ErrorVendorOpentok,
  'error.vendor.recaptcha': ErrorVendorRecaptcha,
  'error.vendor.s3': ErrorVendorS3,
  'error.vendor.stripe': ErrorVendorStripe,
  'error.vendor.stripe.card': ErrorVendorStripeCard,
  'warning.rpc.deprecated': WarningRpcDeprecated,
} as const;

/**
 * Turns source api response error into corresponding error instance.
 * instance has the same code and message as defined in backend api v9.0.
 * @example
 * const errorResponse = await apiRequest(badParams);
 * const error = parseBackendErrorFactory(errorResponse, badParams);
 * if(error instance of WantedRpcError){do something}
 * @param rpcError - error we get from api as response
 * @param sentParams - params of the request that raised error
 */
export const parseRpcErrorFactory = (rpcError: RpcError, sentParams: any) => {
  const Class = apiErrorsMap[rpcError.code];
  const error = Class
    ? new Class(rpcError, sentParams)
    : new RpcRequestError(rpcError, sentParams);

  switch (error.reason) {
    case REASON_SUBSCRIPTION_EXPIRED: {
      return new ErrorSubscriptionExpired(rpcError, sentParams);
    }
    default: {
      return error;
    }
  }
};
